import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  TagIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import { API_URL } from '../../../api/config';

interface SupplierProfile {
  id: number;
  tenant_id: number;
  business_name: string;
  business_type: string;
  registration_number: string | null;
  tax_id: string | null;
  description: string | null;
  contact_email: string;
  contact_phone: string;
  contact_person: string;
  address: string;
  city: string;
  country: string;
  logo_url: string | null;
  website: string | null;
  verification_status: string;
  verification_submitted_at: string | null;
  verified_at: string | null;
  verified_by: number | null;
  verification_reason: string | null;
  products: string[];
  created_at: string;
  updated_at: string;
  tenant_name: string;
  marketplace_access_status: string;
  marketplace_participation_type: string | null;
  listing_count: number;
  member_count: number;
}

const MarketplaceSuppliersPage: React.FC = () => {
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<SupplierProfile | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/super-admin/marketplace/supplier-profiles?${params}`, { headers });
      const data = await res.json();
      setSuppliers(data.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, headers]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleReview = async (profileId: number, decision: 'approved' | 'rejected', reason?: string) => {
    try {
      setActionLoading(true);
      setActionMsg(null);
      const res = await fetch(`${API_URL}/api/super-admin/marketplace/verifications/${profileId}/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ decision, reason: reason || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setActionMsg({ type: 'err', text: data.error || 'Action failed' });
        return;
      }
      setActionMsg({ type: 'ok', text: decision === 'approved' ? 'Supplier approved!' : 'Supplier rejected' });
      fetchSuppliers();
      // Update selected if it was the one acted on
      if (selected?.id === profileId) {
        setSelected({ ...selected, verification_status: decision === 'approved' ? 'verified' : 'rejected' });
      }
    } catch (err: any) {
      setActionMsg({ type: 'err', text: err.message || 'Action failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      submitted: 'bg-amber-100 text-amber-700 border-amber-200',
      under_review: 'bg-blue-100 text-blue-700 border-blue-200',
      draft: 'bg-gray-100 text-gray-600 border-gray-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      enabled: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      not_enabled: 'bg-gray-100 text-gray-500 border-gray-200',
    };
    return styles[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <BuildingStorefrontIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Suppliers</h1>
              <p className="text-emerald-100 mt-1">Registered supplier organisations and their verification status</p>
            </div>
          </div>
          <button onClick={fetchSuppliers} className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or contact person..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm"
          >
            <option value="">All Statuses</option>
            <option value="verified">Verified</option>
            <option value="submitted">Pending Review</option>
            <option value="under_review">Under Review</option>
            <option value="draft">Draft</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchSuppliers} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <BuildingStorefrontIcon className="w-16 h-16 text-gray-200 mx-auto" />
          <p className="text-gray-400 mt-4 font-medium">No suppliers found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* List */}
          <div className="lg:col-span-2 space-y-3 max-h-[70vh] overflow-y-auto">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-1">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</p>
            {suppliers.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelected(s); setActionMsg(null); }}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                  selected?.id === s.id
                    ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-md shrink-0">
                    <BuildingStorefrontIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900 text-sm truncate">{s.business_name}</p>
                    <p className="text-xs text-gray-500 truncate">{s.tenant_name} · {s.contact_person}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadge(s.verification_status)}`}>
                    {s.verification_status}
                  </span>
                  <span className="text-xs text-gray-400">{s.country}</span>
                  <span className="text-xs text-gray-400">{s.listing_count} listings</span>
                  <span className="text-xs text-gray-400">{s.member_count} members</span>
                </div>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3">
            {selected ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-5 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <BuildingStorefrontIcon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-extrabold text-gray-900">{selected.business_name}</h2>
                        <p className="text-sm text-gray-500">{selected.tenant_name} · {selected.business_type}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                      <XMarkIcon className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                {selected.verification_status === 'submitted' && (
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                    <button
                      onClick={() => handleReview(selected.id, 'approved')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold text-sm disabled:opacity-50 transition-all"
                    >
                      <CheckCircleIcon className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Approve Supplier'}
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Rejection reason (optional):');
                        handleReview(selected.id, 'rejected', reason || undefined);
                      }}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 font-bold text-sm disabled:opacity-50 transition-all"
                    >
                      <XCircleIcon className="w-5 h-5" />
                      Reject
                    </button>
                  </div>
                )}

                {/* Action message */}
                {actionMsg && (
                  <div className={`mx-6 mt-4 p-3 rounded-xl text-sm font-medium ${actionMsg.type === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {actionMsg.text}
                  </div>
                )}

                {/* Info grid */}
                <div className="p-6 space-y-5">
                  {/* Status badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusBadge(selected.verification_status)}`}>
                      Verification: {selected.verification_status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusBadge(selected.marketplace_access_status)}`}>
                      Marketplace: {selected.marketplace_access_status}
                    </span>
                    {selected.registration_number && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold border bg-gray-100 text-gray-600 border-gray-200">
                        Reg: {selected.registration_number}
                      </span>
                    )}
                  </div>

                  {/* Contact */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Contact Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <UserGroupIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="font-medium">{selected.contact_person}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <EnvelopeIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span>{selected.contact_email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span>{selected.contact_phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span>{selected.address}, {selected.city}, {selected.country}</span>
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  {selected.products && selected.products.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Products</h4>
                      <div className="flex flex-wrap gap-2">
                        {selected.products.map((p, i) => (
                          <span key={i} className="flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                            <TagIcon className="w-3 h-3" />
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-emerald-600">{selected.listing_count}</p>
                      <p className="text-xs text-gray-500 font-medium mt-1">Listings</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-blue-600">{selected.member_count}</p>
                      <p className="text-xs text-gray-500 font-medium mt-1">Members</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-purple-600">{selected.marketplace_participation_type || '—'}</p>
                      <p className="text-xs text-gray-500 font-medium mt-1">Type</p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Timeline</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ClockIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span>Created: {formatDate(selected.created_at)}</span>
                      </div>
                      {selected.verification_submitted_at && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4 text-gray-400 shrink-0" />
                          <span>Submitted for review: {formatDate(selected.verification_submitted_at)}</span>
                        </div>
                      )}
                      {selected.verified_at && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600">
                          <ShieldCheckIcon className="w-4 h-4 shrink-0" />
                          <span className="font-medium">Verified: {formatDate(selected.verified_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {selected.description && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Description</h4>
                      <p className="text-sm text-gray-700">{selected.description}</p>
                    </div>
                  )}

                  {/* Tax ID */}
                  {selected.tax_id && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Tax ID</h4>
                      <p className="text-sm text-gray-700 font-mono">{selected.tax_id}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
                <BuildingStorefrontIcon className="w-16 h-16 text-gray-200 mx-auto" />
                <p className="text-gray-400 mt-4 font-medium">Select a supplier to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceSuppliersPage;
